import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { FolderPlus, UploadCloud02 } from '@untitled-ui/icons-react'
import { MyButton } from '@interstellar-component'
import { collectDroppedFiles, pickedFiles } from '@src/services/droppedFiles'
import { MAX_DROP_FILES } from '../constants'

/**
 * One drop area for every file of the batch: KTP photos and documents, loose
 * or in folders (walked recursively), or picked with "Choose files" / "Choose
 * folder". Hands over the files as they are (junk skipped, at most
 * MAX_DROP_FILES); the grid decides what each one is.
 *
 * Not MyDropzone: it takes one type list and one size limit, lists every file
 * as a card, and rewrites some files before handing them over.
 */
export default function FileDropArea({ onFiles, disabled = false, accept, hint }) {
  const [over, setOver] = useState(false)
  // A dropped folder is being walked (a big one takes a moment).
  const [reading, setReading] = useState(false)
  // The last drop or pick held no file to use (an empty folder, system files only).
  const [empty, setEmpty] = useState(false)
  const filesInput = useRef(null)
  const folderInput = useRef(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // React does not pass webkitdirectory through as a known attribute.
  useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '')
    folderInput.current?.setAttribute('directory', '')
  }, [])

  // Files dropped next to the area would make the browser open the file in
  // this tab, leaving the page (and the files not saved yet).
  useEffect(() => {
    const keep = (e) => {
      if (Array.from(e.dataTransfer?.types || []).includes('Files')) e.preventDefault()
    }
    window.addEventListener('dragover', keep)
    window.addEventListener('drop', keep)
    return () => {
      window.removeEventListener('dragover', keep)
      window.removeEventListener('drop', keep)
    }
  }, [])

  const onPick = (e) => {
    const picked = e.target.files?.length || 0
    const { files, truncated } = pickedFiles(e.target.files, { max: MAX_DROP_FILES })
    e.target.value = '' // the same folder picked again still fires change
    setEmpty(picked > 0 && !files.length && !truncated)
    if (files.length || truncated) onFiles(files, { truncated, unreadable: 0 })
  }

  const onDrop = (e) => {
    e.preventDefault()
    setOver(false)
    if (disabled || reading) return
    setEmpty(false)
    setReading(true)
    // Items are read inside the event (collectDroppedFiles takes them synchronously).
    collectDroppedFiles(e.dataTransfer, { max: MAX_DROP_FILES })
      .then(({ files, truncated, unreadable }) => {
        if (!mounted.current) return
        setEmpty(!files.length && !truncated && !unreadable)
        if (files.length || truncated || unreadable) onFiles(files, { truncated, unreadable })
      })
      .catch(() => mounted.current && onFiles([], { truncated: false, unreadable: 1 }))
      .finally(() => mounted.current && setReading(false))
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) {
          e.dataTransfer.dropEffect = 'copy'
          setOver(true)
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOver(false)
      }}
      onDrop={onDrop}
      aria-disabled={disabled}
      className={`flex flex-col items-center gap-3 rounded-xl border-2 border-dashed px-6 py-6 text-center transition-colors ${
        disabled
          ? 'border-gray-200 bg-gray-50 opacity-60'
          : over
            ? 'border-brand/500 bg-brand/25'
            : 'border-gray-300 bg-white'
      }`}
    >
      <div className="flex size-10 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm">
        <UploadCloud02 className="size-5 text-gray-600" stroke="currentColor" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-gray-900">Drop KTP photos and documents here, or a whole folder</p>
        {hint && <div className="text-xs text-gray-500">{hint}</div>}
      </div>
      {reading && (
        <p role="status" className="text-sm text-gray-600">
          Reading the dropped files…
        </p>
      )}
      {!reading && empty && (
        <p role="status" className="text-sm text-warning/700">
          No file to add: what you dropped holds no file (system files such as .DS_Store are skipped).
        </p>
      )}
      <div className="flex flex-wrap justify-center gap-2">
        <MyButton
          type="button"
          color="secondary"
          variant="outlined"
          size="sm"
          customClassname="gap-1.5"
          disabled={disabled || reading}
          onClick={() => filesInput.current?.click()}
        >
          <UploadCloud02 className="size-4" stroke="currentColor" /> Choose files
        </MyButton>
        <MyButton
          type="button"
          color="secondary"
          variant="outlined"
          size="sm"
          customClassname="gap-1.5"
          disabled={disabled || reading}
          onClick={() => folderInput.current?.click()}
        >
          <FolderPlus className="size-4" stroke="currentColor" /> Choose folder
        </MyButton>
      </div>
      <input
        ref={filesInput}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={onPick}
        data-testid="bulk-files-input"
      />
      <input ref={folderInput} type="file" multiple className="hidden" onChange={onPick} data-testid="bulk-folder-input" />
    </div>
  )
}

FileDropArea.propTypes = {
  onFiles: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
  hint: PropTypes.node,
}
